/**
 * Employer outbox — every outreach request an employer has sent, with
 * status pills, the candidate's reply (when accepted), and quick links back
 * to the Discover directory.
 *
 * Gated to verified employers via EmployerProtected in App.js.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, ChevronDown, ChevronUp, Clock, User,
  Briefcase, Loader, Inbox, CheckCircle2, XCircle, LogOut, Mail,
} from "lucide-react";
import { useEmployer } from "../components/employer/EmployerProtected";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmployerRequests() {
  const navigate = useNavigate();
  const { employer } = useEmployer();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, accepted: 0, declined: 0, expired: 0 });
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [digestOptin, setDigestOptin] = useState(true);
  const [digestSaving, setDigestSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/employer/contact-requests`,
        { withCredentials: true });
      setRows(r.data.requests || []);
      setCounts(r.data.counts || {});
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load current digest opt-in
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/employer/me/preferences`,
          { withCredentials: true });
        setDigestOptin(r.data?.digest_optin !== false);
      } catch { /* ignore */ }
    })();
  }, []);

  const toggleDigest = async () => {
    const next = !digestOptin;
    setDigestSaving(true);
    setDigestOptin(next);  // optimistic
    try {
      await axios.patch(`${BACKEND_URL}/api/employer/me/preferences`,
        { digest_optin: next }, { withCredentials: true });
    } catch {
      // rollback on error
      setDigestOptin(!next);
    } finally {
      setDigestSaving(false);
    }
  };

  const logout = async () => {
    try { await axios.post(`${BACKEND_URL}/api/employer/logout`, {}, { withCredentials: true }); } catch { /* ignore */ }
    navigate("/employer/login", { replace: true });
  };

  const filtered = statusFilter === "all"
    ? rows
    : rows.filter((r) => r.status === statusFilter);

  const TABS = [
    { id: "all",      label: "All",      color: "#0F1B2D" },
    { id: "pending",  label: "Pending",  color: "#FF6A00" },
    { id: "accepted", label: "Accepted", color: "#0E5A4A" },
    { id: "declined", label: "Declined", color: "#B91C1C" },
  ];

  return (
    <div className="min-h-screen" style={{ background: "#F8FAFC" }}>
      {/* Header — mirrors /discover so the employer nav feels consistent */}
      <div style={{ background: "#0F1B2D" }}>
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div>
            <div className="font-mono-rm text-[10px] uppercase tracking-widest"
                 style={{ color: "#FFC145", fontWeight: 700 }}>
              · Resmo Talent Directory · Outbox
            </div>
            <div className="font-display uppercase leading-tight"
                 style={{ fontSize: "1.35rem", color: "white", letterSpacing: "-0.01em" }}>
              Your requests
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/discover"
                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
                  style={{ background: "transparent", color: "#FFC145",
                           border: "1px solid #FFC145", fontWeight: 700, textDecoration: "none" }}
                  data-testid="employer-requests-back-to-discover">
              <ArrowLeft size={11} /> Directory
            </Link>
            <button onClick={logout}
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
                    style={{ background: "transparent", color: "#FFC145",
                             border: "1px solid #FFC145", fontWeight: 700 }}
                    data-testid="employer-requests-logout">
              <LogOut size={11} /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-6">
        <div className="text-[13px] mb-4 flex flex-wrap items-center justify-between gap-3"
             style={{ color: "#475569" }}>
          <span>Signed in as <b>{employer?.email}</b> · {employer?.company}</span>
          <button
            type="button"
            onClick={toggleDigest}
            disabled={digestSaving}
            className="inline-flex items-center gap-2 rounded px-2.5 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
            style={{
              background: digestOptin ? "#FFF4E0" : "white",
              color: digestOptin ? "#B45309" : "#94A3B8",
              border: `1.5px solid ${digestOptin ? "#FFC145" : "#CBD5E1"}`,
              fontWeight: 700, cursor: digestSaving ? "wait" : "pointer",
            }}
            data-testid="employer-digest-toggle"
            title={digestOptin
              ? "Weekly digest email is ON — click to opt out"
              : "Weekly digest email is OFF — click to opt in"}
          >
            <Mail size={12} />
            {digestOptin ? "· Weekly digest · ON" : "· Weekly digest · OFF"}
            <span style={{
              width: 22, height: 12, borderRadius: 6,
              background: digestOptin ? "#FF6A00" : "#CBD5E1",
              position: "relative", display: "inline-block",
            }}>
              <span style={{
                position: "absolute", top: 1, left: digestOptin ? 11 : 1,
                width: 10, height: 10, borderRadius: 5, background: "white",
                transition: "left 120ms ease-out",
              }} />
            </span>
          </button>
        </div>

        {/* Status filter chips */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="employer-requests-tabs">
          {TABS.map((t) => {
            const isActive = statusFilter === t.id;
            const count = t.id === "all"
              ? rows.length
              : counts[t.id] || 0;
            return (
              <button key={t.id} onClick={() => setStatusFilter(t.id)}
                      className="px-3 py-1 rounded text-[11px] font-mono-rm uppercase tracking-widest inline-flex items-center gap-1.5"
                      style={{
                        background: isActive ? t.color : "white",
                        color: isActive ? "white" : t.color,
                        border: `1.5px solid ${t.color}`,
                        fontWeight: 700, cursor: "pointer",
                      }}
                      data-testid={`employer-requests-tab-${t.id}`}>
                {t.label}
                <span style={{
                  background: isActive ? "rgba(255,255,255,0.18)" : t.color + "20",
                  padding: "1px 6px", borderRadius: 4, fontSize: 10,
                }}>{count}</span>
              </button>
            );
          })}
        </div>

        {loading && (
          <div className="text-center py-12">
            <Loader className="mx-auto animate-spin" size={22} style={{ color: "#FF6A00" }} />
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="text-center py-12 rounded-md"
               style={{ background: "white", border: "1.5px dashed #CBD5E1" }}
               data-testid="employer-requests-empty">
            <Inbox size={26} className="mx-auto mb-2" style={{ color: "#94A3B8" }} />
            <div className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#94A3B8" }}>
              · Nothing here
            </div>
            <div className="text-[13px] mt-1" style={{ color: "#475569" }}>
              {statusFilter === "all"
                ? "You haven't sent any contact requests yet. "
                : `No ${statusFilter} requests. `}
              <Link to="/discover" style={{ color: "#FF6A00", textDecoration: "underline", fontWeight: 700 }}>
                Browse the directory →
              </Link>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {filtered.map((r) => (
            <RequestRow key={r.id}
                        req={r}
                        expanded={expandedId === r.id}
                        onToggle={() => setExpandedId((prev) => prev === r.id ? null : r.id)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function RequestRow({ req, expanded, onToggle }) {
  return (
    <div className="rounded-md overflow-hidden"
         style={{ background: "white", border: "1.5px solid #CBD5E1" }}
         data-testid={`employer-request-${req.id}`}>
      <div className="flex items-start justify-between gap-3 px-4 py-3">
        <Link to={`/employer/requests/${req.id}`}
              className="flex-1 min-w-0 hover:opacity-80"
              style={{ textDecoration: "none", color: "inherit" }}
              data-testid={`employer-request-open-${req.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill status={req.status} />
            <span className="font-display uppercase leading-tight"
                  style={{ fontSize: "0.98rem", color: "#0F1B2D", letterSpacing: "-0.01em" }}>
              {req.candidate_display}
            </span>
          </div>
          <div className="text-[13px] mt-1" style={{ color: "#334155" }}>
            <Briefcase size={12} className="inline mr-1" style={{ color: "#FF6A00" }} />
            <b>{req.role_title}</b>
          </div>
          <div className="text-[11px] mt-1" style={{ color: "#94A3B8" }}>
            <Clock size={10} className="inline mr-1" />
            Sent {new Date(req.created_at).toLocaleDateString()}
            {req.responded_at && (
              <> · Answered {new Date(req.responded_at).toLocaleDateString()}</>
            )}
          </div>
        </Link>
        <button type="button" onClick={onToggle}
                className="p-1 rounded self-start"
                style={{ color: "#0F1B2D", background: "transparent",
                         border: "1px solid #CBD5E1", flexShrink: 0 }}
                title={expanded ? "Collapse" : "Expand inline"}
                data-testid={`employer-request-toggle-${req.id}`}>
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>
      {expanded && (
        <div className="px-4 pb-4" style={{ borderTop: "1px dashed #CBD5E1" }}>
          <div className="font-mono-rm text-[9px] uppercase tracking-widest mt-3 mb-1"
               style={{ color: "#94A3B8", fontWeight: 700 }}>
            · Your message
          </div>
          <div className="text-[13px] leading-relaxed whitespace-pre-wrap"
               style={{ background: "#F8FAFC", border: "1px dashed #CBD5E1",
                        padding: "10px 12px", borderRadius: 6, color: "#334155" }}>
            {req.message}
          </div>

          {req.candidate_reply && (
            <>
              <div className="font-mono-rm text-[9px] uppercase tracking-widest mt-3 mb-1"
                   style={{ color: "#0E5A4A", fontWeight: 700 }}>
                <User size={10} className="inline mr-1" />
                · {req.candidate_display}&apos;s reply
              </div>
              <div className="text-[13px] leading-relaxed whitespace-pre-wrap"
                   style={{ background: "#D1FAE5", border: "1px dashed #0E5A4A",
                            padding: "10px 12px", borderRadius: 6, color: "#064E3B" }}
                   data-testid={`employer-request-reply-${req.id}`}>
                {req.candidate_reply}
              </div>
            </>
          )}

          {req.status === "accepted" && !req.candidate_reply && (
            <div className="text-[12px] italic mt-3 rounded px-3 py-2"
                 style={{ background: "#D1FAE5", color: "#064E3B", border: "1px dashed #0E5A4A" }}>
              <CheckCircle2 size={12} className="inline mr-1" />
              Accepted — no reply. The candidate should reach out via email.
            </div>
          )}
          {req.status === "declined" && (
            <div className="text-[12px] italic mt-3 rounded px-3 py-2"
                 style={{ background: "#FEE2E2", color: "#7C2D12", border: "1px dashed #B91C1C" }}>
              <XCircle size={12} className="inline mr-1" />
              Not a fit right now. Please respect the candidate&apos;s decision.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const meta = ({
    pending:  { bg: "#FFF4E0", color: "#FF6A00", label: "Pending" },
    accepted: { bg: "#D1FAE5", color: "#0E5A4A", label: "Accepted" },
    declined: { bg: "#FEE2E2", color: "#B91C1C", label: "Declined" },
    expired:  { bg: "#F1F5F9", color: "#64748B", label: "Expired" },
  }[status] || { bg: "#F1F5F9", color: "#64748B", label: status });
  return (
    <span className="text-[9px] font-mono-rm uppercase tracking-widest px-1.5 py-0.5 rounded"
          style={{ background: meta.bg, color: meta.color, fontWeight: 700 }}>
      {meta.label}
    </span>
  );
}
